import React, { useState, useEffect, useMemo } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Box,
	Typography,
	CircularProgress,
	Divider,
	Autocomplete,
	TextField,
	Collapse,
	IconButton,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CommentEditor from 'containers/pages/comments/components/editor/CommentEditor';
import useCommentEditor from 'containers/pages/comments/hooks/useCommentEditor';
import { CANtrackComment, CANtrackCase } from 'containers/pages/comments/types/comment';
import { convertToCANtrackCase } from 'containers/pages/comments/utils/convertToCANtrackCase';
import getCase from 'api/comments/getCase';
import getStatuses, { CANtrackStatus } from 'api/comments/getStatuses';
import updateCase from 'api/comments/updateCase';
import useNotificationProvider from 'hooks/notificationBar/useNotificationProvider';

interface CompletionDialogProps {
	open: boolean;
	caseId: number;
	caseTitle: string;
	taskTitle: string;
	onClose: () => void;
	onComplete: () => Promise<void>;
}

const CompletionDialog: React.FC<CompletionDialogProps> = ({
	open,
	caseId,
	caseTitle,
	taskTitle,
	onClose,
	onComplete,
}) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const { notifySuccess, notifyError } = useNotificationProvider();

	// Loading / submission state
	const [loadingData, setLoadingData] = useState(true);
	const [submitting, setSubmitting] = useState(false);

	// Case data
	const [fullCase, setFullCase] = useState<CANtrackCase | null>(null);

	// Status fields
	const [statuses, setStatuses] = useState<CANtrackStatus[]>([]);
	const [selectedStatus, setSelectedStatus] = useState<CANtrackStatus | null>(null);
	const [statusComment, setStatusComment] = useState('');
	const [originalStatusId, setOriginalStatusId] = useState<string | null>(null);
	const [originalStatusComment, setOriginalStatusComment] = useState('');

	// Comment section toggle
	const [commentExpanded, setCommentExpanded] = useState(false);

	// Comment form - local no-op state since we don't maintain a list
	const [, setComments] = useState<CANtrackComment[]>([]);

	// Memoize to avoid infinite re-render loop (same issue as CANtrackCommentDialog)
	const initialComment: CANtrackComment | undefined = useMemo(() => {
		if (!fullCase) return undefined;
		return {
			id: 0,
			title: taskTitle,
			description: '',
			date: new Date(),
			timeRegular: 0.1,
			timeOver: 0,
			caseID: fullCase.id,
			caseTitle: fullCase.title,
			customerID: fullCase.customerID,
			customerName: fullCase.customerName,
			gpda: {
				group: fullCase.gpd.group,
				project: fullCase.gpd.project,
				deliverable: fullCase.gpd.deliverable,
				activity: { id: 0, title: '' },
			},
			billable: true,
		};
	}, [fullCase, taskTitle]);

	const {
		formState,
		errors,
		handleChange,
		handleSaveComment,
		handleResetComment,
	} = useCommentEditor(setComments, initialComment);

	// Fetch case details + statuses on open
	useEffect(() => {
		if (!open) {
			setFullCase(null);
			setLoadingData(true);
			setCommentExpanded(false);
			return;
		}

		let cancelled = false;

		async function fetchData() {
			setLoadingData(true);
			try {
				const [caseRaw, statusList] = await Promise.all([
					getCase(caseId),
					getStatuses(),
				]);

				if (cancelled) return;

				const converted = convertToCANtrackCase(caseRaw);
				setFullCase(converted);
				setStatuses(statusList);

				if (converted.statusId) {
					const current = statusList.find(
						(s) => parseInt(s.Id) === converted.statusId,
					);
					if (current) {
						setSelectedStatus(current);
						setOriginalStatusId(current.Id);
					}
				}

				const comment = converted.statusComment || '';
				setStatusComment(comment);
				setOriginalStatusComment(comment);
			} catch (error) {
				console.error('[CompletionDialog] Failed to fetch completion data:', error);
				notifyError('Failed to load case details');
			} finally {
				if (!cancelled) setLoadingData(false);
			}
		}

		fetchData();
		return () => {
			cancelled = true;
		};
	}, [open, caseId]);

	const hasStatusChanges =
		selectedStatus?.Id !== originalStatusId ||
		statusComment !== originalStatusComment;

	const hasCommentContent =
		commentExpanded &&
		(formState.title.trim() !== '' || formState.description.trim() !== '');

	const handleSubmit = async () => {
		setSubmitting(true);
		try {
			// 1. Save comment if expanded and has content
			if (hasCommentContent) {
				const success = await handleSaveComment();
				if (!success) {
					setSubmitting(false);
					return;
				}
			}

			// 2. Update case status if changed
			if (hasStatusChanges) {
				await updateCase({
					Id: caseId,
					...(selectedStatus
						? { StatusId: parseInt(selectedStatus.Id) }
						: {}),
					StatusComment: statusComment,
				});
			}

			// 3. Complete the task
			await onComplete();

			notifySuccess('Task completed');
			onClose();
		} catch (error) {
			console.error('[CompletionDialog] Failed to complete task:', error);
			notifyError('Failed to complete task');
		} finally {
			setSubmitting(false);
		}
	};

	const handleSkipAndComplete = async () => {
		setSubmitting(true);
		try {
			await onComplete();
			notifySuccess('Task completed');
			onClose();
		} catch (error) {
			console.error('[CompletionDialog] Failed to complete task:', error);
			notifyError('Failed to complete task');
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="md"
			fullWidth
			fullScreen={isMobile}
		>
			<DialogTitle
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
				}}
			>
				Complete Task
				<IconButton onClick={onClose} edge="end" disabled={submitting}>
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent>
				{loadingData ? (
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							gap: 1,
							py: 4,
						}}
					>
						<CircularProgress size={24} />
						<Typography>Loading case details...</Typography>
					</Box>
				) : fullCase ? (
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
						<Typography variant="body2" color="text.secondary">
							Completing task linked to Case #{caseId}: {caseTitle}
						</Typography>

						{/* Status Section */}
						<Typography variant="subtitle2" color="text.secondary">
							Update Case Status (optional)
						</Typography>
						<Autocomplete
							options={statuses}
							getOptionLabel={(option) => option.Title}
							value={selectedStatus}
							onChange={(_, newValue) => setSelectedStatus(newValue)}
							isOptionEqualToValue={(option, value) =>
								option.Id === value.Id
							}
							size="small"
							renderInput={(params) => (
								<TextField
									{...params}
									label="Case Status"
									variant="outlined"
								/>
							)}
						/>
						<TextField
							label="Status Comment"
							value={statusComment}
							onChange={(e) => setStatusComment(e.target.value)}
							multiline
							minRows={2}
							maxRows={4}
							size="small"
							fullWidth
						/>

						{/* Comment Section */}
						<Divider />
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								cursor: 'pointer',
							}}
							onClick={() => setCommentExpanded(!commentExpanded)}
						>
							<Typography variant="subtitle2" color="text.secondary">
								Log CANtrack Comment (optional)
							</Typography>
							<IconButton size="small">
								{commentExpanded ? (
									<ExpandLessIcon />
								) : (
									<ExpandMoreIcon />
								)}
							</IconButton>
						</Box>
						<Collapse in={commentExpanded}>
							<CommentEditor
								comment={initialComment}
								formState={formState}
								errors={errors}
								handleChange={handleChange}
								handleSave={() => {}} // Save is handled by the dialog submit
								handleReset={handleResetComment}
								handleDelete={() => {}}
								hideFilters
								disableCaseSelection
								linearLayout
								hideButtons
								resizableComment
							/>
						</Collapse>
					</Box>
				) : (
					<Typography color="error" sx={{ py: 2 }}>
						Failed to load case details. Please try again.
					</Typography>
				)}
			</DialogContent>
			<DialogActions>
				<Button
					onClick={handleSkipAndComplete}
					disabled={submitting}
					sx={{ mr: 'auto' }}
				>
					Skip & Complete
				</Button>
				<Button onClick={onClose} disabled={submitting}>
					Cancel
				</Button>
				<Button
					variant="contained"
					color="success"
					onClick={handleSubmit}
					disabled={submitting || loadingData}
					startIcon={
						submitting ? <CircularProgress size={16} /> : undefined
					}
				>
					Complete
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default CompletionDialog;
